import type { ApplicationSiblings, Family, FamilyStatus, PersonKind, Sibling } from '../../services/peopleService';
import type { ChipTone } from '../common/StatusChip';
import { fmtClassName } from '../../utils/pdf/format';
import { placementName } from '../students/profile/enrollmentPlacement';

/** How families and their members are labelled everywhere in the app. */

export const familyName = (f: Pick<Family, 'displayName'>): string => f.displayName?.trim() || 'Unnamed family';

/** `resolved` = staff confirmed the household and its guardians. */
export const FAMILY_STATUS: Record<FamilyStatus, { label: string; tone: ChipTone; hint: string }> = {
  resolved: { label: 'Linked', tone: 'success', hint: 'Guardians confirmed by staff' },
  candidate: { label: 'Not yet linked', tone: 'warning', hint: 'Created from an application; guardians not confirmed yet' },
};

export const PERSON_KIND_LABEL: Record<PersonKind, string> = {
  applicant: 'Applicant',
  student: 'Student',
  alumni: 'Alumni',
};

export const PERSON_KIND_TONE: Record<PersonKind, ChipTone> = {
  applicant: 'info',
  student: 'success',
  alumni: 'neutral',
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : `${n} ${many}`);

/** The header-chip summary of an applicant's siblings: current students
 *  first, then siblings still applying, then alumni. Null when there are none. */
export const siblingSummary = (siblings: Array<Pick<Sibling, 'kind'>>): { label: string; tone: ChipTone } | null => {
  const count = (kind: PersonKind) => siblings.filter(s => s.kind === kind).length;
  const [students, applicants, alumni] = [count('student'), count('applicant'), count('alumni')];
  if (students) return { label: `${plural(students, 'Sibling', 'siblings')} at school`, tone: 'highlight' };
  if (applicants) return { label: plural(applicants, 'Sibling also applying', 'siblings applying'), tone: 'info' };
  if (alumni) return { label: plural(alumni, 'Sibling is alumni', 'alumni siblings'), tone: 'neutral' };
  return null;
};

/** Under a sibling's name: GR and class for a student, application number,
 *  class and cycle for an applicant. */
export const siblingDetails = (s: Sibling): string[] => {
  if (s.application) {
    const name = (ref: unknown) => (ref && typeof ref === 'object' ? (ref as { name?: string }).name : undefined);
    return [s.application.applicationNumber, fmtClassName(name(s.application.classId)), name(s.application.cycleId)].filter(Boolean) as string[];
  }
  const placement = s.enrollment && placementName({ ...s.enrollment, classId: { name: fmtClassName(s.enrollment.classId?.name) } });
  return [s.grNumber && `GR ${s.grNumber}`, placement].filter(Boolean) as string[];
};

/** "father's", "mother's", "parents'" — whose CNIC on the form found the family. */
export const matchedByPhrase = (guardians: ApplicationSiblings['matchedGuardians']): string => {
  const relations = Array.from(new Set(guardians.map(g => g.relation)));
  if (relations.length > 1) return "parents'";
  return `${relations[0] ?? 'guardian'}'s`;
};
